import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Home, Download, BarChart3, CalendarIcon, LogOut, TrendingUp } from 'lucide-react';
import { format, parseISO, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import { tr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const moods = [
  { value: 'awesome', emoji: '😄', label: 'Harika', color: '#fef3c7', textColor: '#92400e' },
  { value: 'good', emoji: '🙂', label: 'İyi', color: '#d1fae5', textColor: '#065f46' },
  { value: 'okay', emoji: '😐', label: 'Normal', color: '#dbeafe', textColor: '#1e40af' },
  { value: 'bad', emoji: '😔', label: 'Kötü', color: '#fce7f3', textColor: '#9f1239' },
  { value: 'awful', emoji: '😢', label: 'Çok Kötü', color: '#f3e8ff', textColor: '#6b21a8' },
];

export default function History({ user, onLogout }) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('calendar');
  const [moodEntries, setMoodEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchMoods();
  }, []);

  const fetchMoods = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/moods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoodEntries(response.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/moods/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mood_history.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} dosyası indirildi!`);
    } catch (error) {
      toast.error('Dışa aktarma başarısız');
    }
  };

  const getMoodForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return moodEntries.find(entry => entry.date === dateStr);
  };

  const getMoodEmoji = (value) => {
    return moods.find(m => m.value === value)?.emoji || '';
  };

  const getMoodLabel = (value) => {
    return moods.find(m => m.value === value)?.label || '';
  };

  const getMoodColor = (value) => {
    return moods.find(m => m.value === value);
  };

  const moodCounts = moods.map(mood => ({
    ...mood,
    count: moodEntries.filter(entry => entry.mood === mood.value).length
  }));

  const totalEntries = moodEntries.length;
  const maxCount = Math.max(...moodCounts.map(m => m.count), 1);

  const getHeatMapData = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => {
      const mood = getMoodForDate(day);
      const moodValue = mood ? getMoodValue(mood.mood) : 0;
      return {
        date: day,
        value: moodValue,
        mood: mood,
        intensity: mood?.intensity || 0
      };
    });
  };

  const getMoodValue = (moodType) => {
    const values = { awesome: 5, good: 4, okay: 3, bad: 2, awful: 1 };
    return values[moodType] || 0;
  };

  const getWeeklyPattern = () => {
    const weekDays = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const pattern = weekDays.map((day, index) => {
      const dayMoods = moodEntries.filter(entry => {
        const date = parseISO(entry.date);
        return date.getDay() === index;
      });
      
      const avgValue = dayMoods.length > 0
        ? dayMoods.reduce((sum, m) => sum + getMoodValue(m.mood), 0) / dayMoods.length
        : 0;
      
      return {
        day,
        count: dayMoods.length,
        avgValue: avgValue.toFixed(1)
      };
    });
    
    return pattern;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="animate-pulse text-lg">Yükleniyor...</div>
      </div>
    );
  }

  const heatMapData = getHeatMapData();
  const weeklyPattern = getWeeklyPattern();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl animate-float">😊</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Ruh Hali Geçmişi</h1>
              <p className="text-sm text-gray-600">Toplam {totalEntries} kayıt</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="home-btn"
              variant="outline"
              onClick={() => navigate('/')}
              className="gap-2 hover-lift"
            >
              <Home className="w-4 h-4" />
              Ana Sayfa
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 animate-slide-up">
          <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
            <div className="flex gap-2">
              <Button
                data-testid="calendar-view-btn"
                onClick={() => setViewMode('calendar')}
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                className="gap-2 hover-lift"
              >
                <CalendarIcon className="w-4 h-4" />
                Takvim
              </Button>
              <Button
                data-testid="heatmap-view-btn"
                onClick={() => setViewMode('heatmap')}
                variant={viewMode === 'heatmap' ? 'default' : 'outline'}
                className="gap-2 hover-lift"
              >
                🔥 Isı Haritası
              </Button>
              <Button
                data-testid="chart-view-btn"
                onClick={() => setViewMode('chart')}
                variant={viewMode === 'chart' ? 'default' : 'outline'}
                className="gap-2 hover-lift"
              >
                <BarChart3 className="w-4 h-4" />
                Grafikler
              </Button>
              <Button
                data-testid="pattern-view-btn"
                onClick={() => setViewMode('pattern')}
                variant={viewMode === 'pattern' ? 'default' : 'outline'}
                className="gap-2 hover-lift"
              >
                <TrendingUp className="w-4 h-4" />
                Pattern
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="export-csv-btn"
                onClick={() => handleExport('csv')}
                variant="outline"
                className="gap-2 hover-lift"
              >
                <Download className="w-4 h-4" />
                CSV
              </Button>
              <Button
                data-testid="export-json-btn"
                onClick={() => handleExport('json')}
                variant="outline"
                className="gap-2 hover-lift"
              >
                <Download className="w-4 h-4" />
                JSON
              </Button>
            </div>
          </div>

          {viewMode === 'calendar' && (
            <div>
              <div className="flex justify-center mb-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={tr}
                  className="rounded-2xl border shadow-sm"
                  modifiers={{
                    hasMood: (date) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return moodEntries.some(entry => entry.date === dateStr);
                    }
                  }}
                  modifiersStyles={{
                    hasMood: {
                      fontWeight: 'bold',
                      backgroundColor: '#e0e7ff'
                    }
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const mood = getMoodForDate(date);
                      return (
                        <div className="relative w-full h-full flex flex-col items-center justify-center">
                          <span>{format(date, 'd')}</span>
                          {mood && (
                            <span className="text-lg absolute -bottom-1 emoji-hover">{getMoodEmoji(mood.mood)}</span>
                          )}
                        </div>
                      );
                    }
                  }}
                />
              </div>

              {selectedDate && getMoodForDate(selectedDate) && (
                <div
                  data-testid="selected-mood-detail"
                  className="mt-6 p-6 rounded-2xl border-2 animate-scale-in hover-lift"
                  style={{
                    backgroundColor: getMoodColor(getMoodForDate(selectedDate).mood)?.color,
                    borderColor: getMoodColor(getMoodForDate(selectedDate).mood)?.textColor
                  }}
                >
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-5xl animate-bounce-slow">{getMoodEmoji(getMoodForDate(selectedDate).mood)}</span>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold">{getMoodLabel(getMoodForDate(selectedDate).mood)}</h3>
                      <p className="text-sm opacity-80">{format(selectedDate, 'dd MMMM yyyy', { locale: tr })}</p>
                      <div className="mt-2 flex gap-4 text-sm">
                        <span>⚡ Yoğunluk: {getMoodForDate(selectedDate).intensity}/10</span>
                        <span>🕐 {getMoodForDate(selectedDate).time_of_day === 'morning' ? 'Sabah' : 
                                  getMoodForDate(selectedDate).time_of_day === 'afternoon' ? 'Öğlen' :
                                  getMoodForDate(selectedDate).time_of_day === 'evening' ? 'Akşam' :
                                  getMoodForDate(selectedDate).time_of_day === 'night' ? 'Gece' : 'Belirtilmemiş'}</span>
                      </div>
                    </div>
                  </div>
                  {getMoodForDate(selectedDate).note && (
                    <p className="text-sm mt-3 p-3 bg-white bg-opacity-50 rounded-lg">
                      {getMoodForDate(selectedDate).note}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {viewMode === 'heatmap' && (
            <div>
              <h3 className="text-xl font-semibold mb-6">Isı Haritası - {format(currentMonth, 'MMMM yyyy', { locale: tr })}</h3>
              <div className="grid grid-cols-7 gap-2 mb-6">
                {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                    {day}
                  </div>
                ))}
                
                {Array.from({ length: startOfMonth(currentMonth).getDay() === 0 ? 6 : startOfMonth(currentMonth).getDay() - 1 }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                
                {heatMapData.map((data, index) => {
                  const moodInfo = getMoodColor(data.mood?.mood);
                  return (
                    <div
                      key={index}
                      data-testid={`heatmap-day-${format(data.date, 'yyyy-MM-dd')}`}
                      className="aspect-square rounded-lg flex items-center justify-center text-sm font-medium hover-grow cursor-pointer transition-all"
                      style={{
                        backgroundColor: data.value > 0 ? moodInfo?.color : '#f3f4f6',
                        opacity: data.value > 0 ? 0.4 + (data.intensity / 20) : 0.3
                      }}
                      title={`${format(data.date, 'dd MMM', { locale: tr })}: ${data.mood ? getMoodLabel(data.mood.mood) : 'Kayıt yok'}`}
                    >
                      {data.value > 0 && (
                        <span className="text-lg">{getMoodEmoji(data.mood.mood)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {viewMode === 'chart' && (
            <div>
              <h3 className="text-xl font-semibold mb-6">Ruh Hali Dağılımı</h3>
              <div className="space-y-4 mb-8">
                {moodCounts.map((mood) => {
                  const percentage = totalEntries > 0 ? Math.round((mood.count / totalEntries) * 100) : 0;
                  const barWidth = maxCount > 0 ? (mood.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={mood.value} className="space-y-2 animate-fade-in">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl emoji-hover">{mood.emoji}</span>
                          <span className="font-medium">{mood.label}</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {mood.count} gün ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full rounded-full progress-bar flex items-center justify-end pr-2"
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: mood.textColor
                          }}
                        >
                          {mood.count > 0 && (
                            <span className="text-white text-xs font-semibold">{mood.count}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Son Kayıtlar</h3>
                <div className="space-y-3">
                  {moodEntries
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 10)
                    .map((entry) => {
                      const mood = moods.find(m => m.value === entry.mood);
                      return (
                        <div
                          key={entry.id}
                          data-testid={`mood-entry-${entry.id}`}
                          className="p-4 rounded-xl border-2 hover-lift cursor-pointer animate-fade-in"
                          style={{
                            backgroundColor: mood.color,
                            borderColor: mood.textColor
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{mood.emoji}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{mood.label}</span>
                                <span className="text-xs px-2 py-1 bg-white bg-opacity-50 rounded">
                                  ⚡ {entry.intensity}/10
                                </span>
                              </div>
                              <div className="text-sm opacity-80">
                                {format(parseISO(entry.date), 'dd MMMM yyyy', { locale: tr })}
                              </div>
                              {entry.note && (
                                <p className="text-sm mt-2 opacity-90">{entry.note}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {viewMode === 'pattern' && (
            <div>
              <h3 className="text-xl font-semibold mb-6">Haftalık Pattern Analizi</h3>
              <div className="space-y-4">
                {weeklyPattern.map((day, index) => {
                  const maxDayCount = Math.max(...weeklyPattern.map(d => d.count), 1);
                  const barWidth = (day.count / maxDayCount) * 100;
                  const avgColor = day.avgValue >= 4 ? '#065f46' : day.avgValue >= 3 ? '#1e40af' : '#9f1239';
                  
                  return (
                    <div key={day.day} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium w-28">{day.day}</span>
                          <span className="text-sm text-gray-600">{day.count} kayıt</span>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: avgColor }}>
                          Ort: {day.avgValue}/5
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full rounded-full progress-bar"
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: avgColor
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
                <h4 className="font-semibold mb-3">💡 İçgörüler</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  {weeklyPattern.filter(d => d.count > 0).length > 0 ? (
                    <>
                      <li>
                        • En iyi gününüz: <strong>{weeklyPattern.reduce((prev, curr) => 
                          parseFloat(curr.avgValue) > parseFloat(prev.avgValue) ? curr : prev
                        ).day}</strong>
                      </li>
                      <li>
                        • En çok kayıt yaptığınız gün: <strong>{weeklyPattern.reduce((prev, curr) => 
                          curr.count > prev.count ? curr : prev
                        ).day}</strong>
                      </li>
                    </>
                  ) : (
                    <li>Daha fazla veri toplandıkça içgörüler burada görünecek!</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
